<template>

  <div class="background-wrapper">

    <v-container max-width="900" class="main-container">

      <!-- Logo -->
      <div class="landing-logo">
        <span v-if="BRAND === 'hockeybook'" class="text-logo">
          <span class="text-logo-base">hockey</span><span class="text-logo-accent">book</span>
        </span>
        <img
          v-else
          :src="`brand/${BRAND}/logo.webp`"
          alt="Logo"
          height="80"
        />
      </div>

      <v-row class="pa-0 ma-0">

        <!-- Public events for unauthenticated hockeybook users -->
        <PublicEvents v-if="!userStore.isAuthenticated && BRAND === 'hockeybook'" :searchQuery="publicSearchQuery" :filters="eventFilters" />

        <Calendar v-if="userStore.isAuthenticated || BRAND === 'modernhockey'" />


      </v-row>
    </v-container>

    <!--
    <PartnersMH v-if="BRAND === 'modernhockey'" />
    <Partners v-else />
    -->
    <ProductTiles v-if="BRAND === 'modernhockey'" />
    <Superliga v-if="BRAND === 'modernhockey'" />
    <Teams v-if="BRAND === 'modernhockey'" />
    <ReferenceTiles v-if="BRAND === 'modernhockey'" />

    <!-- Event Form Modal -->
    <EventFormModal
      v-model="showEventFormModal"
      :initialDate="selectedDate"
      @saved="onEventSaved"
    />

    <!-- Event Filter Sheet (unauthenticated) -->
    <EventFilterSheet
      v-if="showPublicFilter"
      v-model="showEventFilterSheet"
      :filters="eventFilters"
      @apply="onApplyEventFilters"
    />

  </div>

</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useUserStore } from '@/stores/user/userStore';
import { useMessageStore } from '@/stores/message/messageStore';
import { useAppStore } from '@/stores/app/appStore';
import { useRouter, useRoute } from 'vue-router';
import { useTheme } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/access.composable';
import { getUserAvatarUrl } from '@/utils/imageUtils';
import { canAddEvents } from '@/utils/userUtils';
import { useNavTransition } from '@/composables/useNavTransition';
import { useDisplay } from 'vuetify';
import axiosInstance from '@/services/axiosInstance';

import Calendar from '@/components/LandingComponents/Calendar.vue';
import Partners from '@/components/LandingComponents/Partners.vue';
import PartnersMH from '@/components/LandingComponents/PartnersMH.vue';
import Superliga from '@/components/LandingComponents/Superliga.vue';
import Teams from '@/components/LandingComponents/Teams.vue';
import LandingHeader from '@/components/LandingComponents/LandingHeader.vue';
import ReferenceTiles from './LandingComponents/ReferenceTiles.vue';
import ProductTiles from '@/components/LandingComponents/ProductTiles.vue';
import UserButtons from '@/components/LandingComponents/UserButtons.vue';
import PublicEvents from '@/components/LandingComponents/PublicEvents.vue';
import { useQuickActions } from '@/composables/useQuickActions'
import type { QuickActionItem } from '@/stores/quickActions/quickActionsStore'
import EventFormModal from '@/components/EventFormModal.vue';
import EventFilterSheet from '@/components/EventFilterSheet.vue';
import type { EventFilters } from '@/components/EventFilterSheet.vue';

const BRAND = import.meta.env.VITE_BRAND as string;

const userStore = useUserStore();
const messageStore = useMessageStore();
const appStore = useAppStore();
const router = useRouter();
const route = useRoute();
const theme = useTheme();
const { t } = useI18n();
const { canCreateContent } = usePermissions();
const { mobile, mdAndUp } = useDisplay();
const { activeTab } = useNavTransition();

const isDarkMode = computed(() => theme.global.current.value.dark);
const showEventFormModal = ref(false);
const selectedDate = ref<Date | null>(null);
const canAddEventAllowed = ref(false);


// ── Public event search ──
const publicSearchQuery = ref('');
const showPublicSearch = computed(() => !userStore.isAuthenticated && BRAND === 'hockeybook' && !isEventRoute.value);

function onEventSearch(q: string) {
  publicSearchQuery.value = q;
}

function onEventSearchClose() {
  publicSearchQuery.value = '';
}

// ── Public event filters ──
const showPublicFilter = computed(() => !userStore.isAuthenticated && BRAND === 'hockeybook' && !isEventRoute.value);
const showEventFilterSheet = ref(false);
const eventFilters = ref<EventFilters>({
  city: '',
  ageGroup: [],
  recruitment: null,
});

const hasActiveEventFilters = computed(() => {
  const f = eventFilters.value;
  return !!(f.city || f.ageGroup.length > 0 || f.recruitment !== null);
});

function onFilterClick() {
  showEventFilterSheet.value = true;
}

function onApplyEventFilters(filters: EventFilters) {
  eventFilters.value = filters;
}

// ── Route-aware checks ──
const isEventRoute = computed(() => route.path.startsWith('/event/'));
const isOnMainRoute = computed(() => ['/', '/teams', '/leagues'].includes(route.path));
const isDetailRoute = computed(() => {
  const path = route.path;
  return path.startsWith('/event/') || path.startsWith('/training/');
});
const primaryIcon = computed(() => 
  appStore.quickActionPrimary?.icon ?? (isDetailRoute.value ? 'mdi-chevron-left' : 'mdi-plus')
)

function onPrimaryClick() {
  if (appStore.quickActionPrimary?.handler) {
    appStore.quickActionPrimary.handler()
    return
  }
  if (isDetailRoute.value) {
    router.back();
  } else {
    showEventFormModal.value = true;
  }
}

function goToLogin() {
  window.location.href = '/login'
}

onMounted(async () => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
});

// Avatar
const userAvatar = computed(() => {
  if (!userStore.user) return null;
  return getUserAvatarUrl(userStore.user);
});

const userInitials = computed(() => {
  if (!userStore.user) return '';
  const names = userStore.user.name?.split(' ') || [];
  return names.map((n: string) => n[0]).join('').toUpperCase();
});

// Quick action items
const quickActionItems = computed<QuickActionItem[]>(() => {
  const items: QuickActionItem[] = [];

  if (userStore.isAuthenticated) {
    items.push({ icon: 'mdi-send', label: `Zprávy${messageStore.unreadCount > 0 ? ` (${messageStore.unreadCount})` : ''}`, action: 'messages' });
    items.push({ icon: 'mdi-badge-account-outline', label: 'Můj profil', action: 'profile' });
    items.push({ icon: 'mdi-account-group', label: 'Moji hráči', action: 'players' });
    items.push({ icon: 'mdi-cog-outline', label: 'Nastavení účtu', action: 'account' });
    items.push({ icon: 'mdi-logout', label: 'Odhlásit', action: 'logout' });
    items.push({ icon: isDarkMode.value ? 'mdi-weather-sunny' : 'mdi-weather-night', label: isDarkMode.value ? 'Světlý režim' : 'Tmavý režim', action: 'toggleTheme' });
  }

  return items;
});

function openMyProfile() {
  if (!userStore.user?._id || !userStore.user?.searchName) return;
  const parts = userStore.user.searchName.split(' ');
  if (parts.length < 2) return;
  router.push(`/users/${userStore.user._id}-${parts[0]}-${parts[1]}`);
}

function onEventSaved(_data: any) {
  // Refresh or navigate as needed after event creation
  router.go(0);
}

function onQuickAction(action: string) {
  // Page-level handler override
  if (appStore.quickActionHandlers[action]) {
    appStore.quickActionHandlers[action]()
    return
  }
  switch (action) {
    case 'joinTeam':
      break;
    case 'messages':
      router.push('/messages');
      break;
    case 'profile':
      openMyProfile();
      break;
    case 'players':
      router.push('/players');
      break;
    case 'logout':
      localStorage.removeItem('token');
      userStore.clearUser();
      window.location.href = '/login';
      break;
    case 'createEvent':
      showEventFormModal.value = true;
      break;
    case 'account':
      router.push('/account');
      break;
    case 'toggleTheme':
      theme.global.name.value = isDarkMode.value ? 'light' : 'customDarkTheme';
      localStorage.setItem('theme', theme.global.name.value);
      break;
    case 'sync-all-attendance':
      if (userStore.user?._id) {
        const feedUrl = `${axiosInstance.defaults.baseURL}/calendar/user/${userStore.user._id}.ics`
        openCalendarFeed(feedUrl)
      }
      break;
  }
}

const qaVisible = computed(() => canCreateContent.value && isOnMainRoute.value && (!mobile.value || activeTab.value === 0 || isDetailRoute.value))
const resolvedItems = computed(() => appStore.quickActionItems ?? quickActionItems.value)

// ── Calendar sync ──
const calendarItems = computed<QuickActionItem[]>(() => {
  if (!userStore.isAuthenticated) return []
  return [
    { icon: 'mdi-calendar-account', label: t('syncAllMyAttendance'), action: 'sync-all-attendance' },
  ]
})

function openCalendarFeed(feedUrl: string) {
  const isApple = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
  const isOutlook = /Outlook|Windows NT/.test(navigator.userAgent)
  if (isApple || isOutlook) {
    window.location.href = `webcal://${feedUrl.replace(/^https?:\/\//, '')}`
    return
  }
  const googleUrl = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(feedUrl)}`
  window.open(googleUrl, '_blank')
}

const qa = useQuickActions({
    visible: qaVisible.value,
    primaryIcon: primaryIcon.value,
    items: resolvedItems.value,
    avatarSrc: userAvatar.value,
    avatarInitials: userInitials.value,
    showFilter: showPublicFilter.value,
    filterActive: hasActiveEventFilters.value,
    showSearch: showPublicSearch.value,
    showCalendar: userStore.isAuthenticated,
    calendarItems: calendarItems.value,
    searchPlaceholder: 'Hledat ligu nebo tým...',
    onPrimary: onPrimaryClick,
    onAction: onQuickAction,
    onFilter: onFilterClick,
    onSearch: onEventSearch,
    onSearchClose: onEventSearchClose,
})
qa.sync(() => qaVisible.value, 'visible')
qa.sync(() => primaryIcon.value, 'primaryIcon')
qa.sync(() => resolvedItems.value, 'items')
qa.sync(() => userAvatar.value, 'avatarSrc')
qa.sync(() => userInitials.value, 'avatarInitials')
qa.sync(() => showPublicFilter.value, 'showFilter')
qa.sync(() => hasActiveEventFilters.value, 'filterActive')
qa.sync(() => showPublicSearch.value, 'showSearch')
qa.sync(() => calendarItems.value, 'calendarItems')
qa.reclaimWhen(() => isOnMainRoute.value && activeTab.value === 0)
</script>

<style scoped>
/* ===== BACKGROUND ===== */
.background-wrapper {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
}

.landing-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.text-logo {
  font-family: 'Barlow Condensed', 'Roboto Condensed', sans-serif;
  font-weight: 900;
  font-size: 42px;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 1;
  color: #ffffff;
  user-select: none;
}

.text-logo-base {
  .v-theme--light & {
    -webkit-text-stroke: 1px #000000;
    paint-order: stroke fill;
  }
}

.text-logo-accent {
  color: rgb(var(--v-theme-ice));
  text-shadow: 0 0 8px rgba(0, 212, 255, 0.6), 0 0 20px rgba(0, 212, 255, 0.3);
}

@media (max-width: 959px) {
  .landing-logo {
    margin-top: 0px;
  }
}

.team-logo {
  object-fit: contain;
}

.fixed-width {
  width: 100px;
  margin: 10px;
}

.blur {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* Pro Safari */
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
}

.appbar {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* Pro Safari */
  background-color: rgba(0, 0, 0, 0.5);
}

.title-logo {

}

.title-margin {
  margin-top: 16px;
}

.custom-card {
  border-radius: 10px;
}



.custom-h1 {
  color: darkslategrey;
}

.legend {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* Pro Safari */
  background-color: rgba(255, 255, 255, 0.3);
  color: black;
}

.mh-blue {
  color: rgba(35, 45, 70, 255);
}

.mh-red {
  color: rgba(163, 10, 54, 255);
}
</style>
